import useIsMobile from "../../lib/useIsMobile"
import { useEffect, useState } from "react"
import { supabase } from "../../lib/supabase"
import { useAuth } from "../../contexts/AuthContext"
import toast from "react-hot-toast"
import { validateFile } from "../../lib/uploadValidation"
import { BadgeIcon, ShieldIcon, DocumentIcon, NoteIcon, WarningIcon } from "../../lib/cccIcons"

export default function DriverVehicle() {
  const isMobile = useIsMobile()
  const { user, profile, updateProfile } = useAuth()
  const [docs, setDocs] = useState([])
  const [form, setForm] = useState({ vehicle_model:"", vehicle_color:"", vehicle_plate:"", vehicle_year:"" })
  const [saving, setSaving] = useState(false)
  const [docForm, setDocForm] = useState({ type:"license", expiry_date:"", file:null })
  const [docUploading, setDocUploading] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (profile) setForm({ vehicle_model:profile?.vehicle_model||"", vehicle_color:profile?.vehicle_color||"", vehicle_plate:profile?.vehicle_plate||"", vehicle_year:profile?.vehicle_year||"" })
    if (user) loadDocs()
    if (user) {
      const sub = supabase.channel("driver-vehicle-live")
        .on("postgres_changes", { event:"*", schema:"public", table:"driver_documents", filter:`driver_id=eq.${user.id}` }, () => loadDocs())
        .subscribe()
      return () => supabase.removeChannel(sub)
    }
  }, [profile, user])

  async function loadDocs() {
    const { data } = await supabase.from("driver_documents").select("*").eq("driver_id", user.id).order("created_at",{ascending:false})
    setDocs(data||[])
    setLoading(false)
  }

  async function saveVehicle(e) {
    e.preventDefault()
    setSaving(true)
    try {
      await updateProfile({ vehicle_model:form.vehicle_model, vehicle_color:form.vehicle_color, vehicle_plate:form.vehicle_plate, vehicle_year:parseInt(form.vehicle_year)||null })
      toast.success("Vehicle info updated")
    } catch(err) { toast.error(err.message) }
    finally { setSaving(false) }
  }

  async function addDocument(e) {
    e.preventDefault()
    if (!docForm.file) return toast.error("Please select a file to upload")
    setDocUploading(true)
    try {
      const ext = docForm.file.name.split(".").pop()
      const path = `${user.id}/${docForm.type}_${Date.now()}.${ext}`
      const { error: upErr } = await supabase.storage.from("driver-documents").upload(path, docForm.file, { upsert:true })
      if (upErr) throw upErr
      const { data: urlData } = supabase.storage.from("driver-documents").getPublicUrl(path)
      const { error } = await supabase.from("driver_documents").insert({
        driver_id: user.id,
        type: docForm.type,
        expiry_date: docForm.expiry_date||null,
        document_url: urlData.publicUrl
      })
      if (error) throw error
      toast.success("Document uploaded!")
      setDocForm({ type:"license", expiry_date:"", file:null })
      loadDocs()
    } catch(err) { toast.error(err.message) }
    finally { setDocUploading(false) }
  }
  async function deleteDoc(id) {
    if (!confirm("Remove this document?")) return
    await supabase.from("driver_documents").delete().eq("id",id).eq("driver_id",user.id)
    toast.success("Document removed")
    loadDocs()
  }

  const inp = { width:"100%", background:"#ffffff", border:"1px solid #e5e5e5", borderRadius:8, padding:"10px 12px", color:"#000000", fontSize:13, outline:"none", fontFamily:"'DM Sans',sans-serif", marginBottom:10 }
  const lbl = { fontSize:11, color:"#666", textTransform:"uppercase", letterSpacing:"0.05em", display:"block", marginBottom:4 }
  const docTypeColor = { license:"#e6821e", insurance:"#1d9e75", registration:"#378add", other:"#888" }

  const isExpiringSoon = (date) => {
    if (!date) return false
    const diff = new Date(date) - new Date()
    return diff > 0 && diff < 30*24*60*60*1000
  }

  const isExpired = (date) => {
    if (!date) return false
    return new Date(date) < new Date()
  }

  return (
    <div>
      <div style={{ background:"#ffffff", border:"1px solid #eeeeee", borderRadius:12, padding:"1.25rem", marginBottom:"1.5rem" }}>
        <div style={{ fontFamily:"Syne", fontSize:14, fontWeight:700, marginBottom:"1rem", color:"#000000" }}>Vehicle information</div>
        <form onSubmit={saveVehicle}>
          <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr":"1fr 1fr", gap:10 }}>
            <div><label style={lbl}>Model</label><input style={inp} placeholder="e.g. Toyota Fielder" value={form.vehicle_model} onChange={e=>setForm(f=>({...f,vehicle_model:e.target.value}))}/></div>
            <div><label style={lbl}>Color</label><input style={inp} placeholder="e.g. White" value={form.vehicle_color} onChange={e=>setForm(f=>({...f,vehicle_color:e.target.value}))}/></div>
            <div><label style={lbl}>License plate</label><input style={inp} placeholder="e.g. KDA 123A" value={form.vehicle_plate} onChange={e=>setForm(f=>({...f,vehicle_plate:e.target.value}))}/></div>
            <div><label style={lbl}>Year</label><input style={inp} type="number" min="1990" max="2026" placeholder="2020" value={form.vehicle_year} onChange={e=>setForm(f=>({...f,vehicle_year:e.target.value}))}/></div>
          </div>
          <button type="submit" disabled={saving}
            style={{ background:saving?"#555555":"#e6821e", border:"none", borderRadius:8, color:saving?"#ccc":"#fff", fontFamily:"Syne,sans-serif", fontSize:13, fontWeight:700, padding:"10px 20px", cursor:saving?"not-allowed":"pointer" }}>
            {saving?"Saving...":"Save vehicle info"}
          </button>
        </form>
      </div>

      <div style={{ background:"#ffffff", border:"1px solid #eeeeee", borderRadius:12, padding:"1.25rem" }}>
        <div style={{ fontFamily:"Syne", fontSize:14, fontWeight:700, marginBottom:"1rem", color:"#000000" }}>Documents</div>
        {loading&&<div style={{ color:"#777777", fontSize:13 }}>Loading...</div>}
        {docs.map(d=>(
          <div key={d.id} style={{ display:"flex", alignItems:"center", gap:12, padding:"0.75rem", background:"#ffffff", borderRadius:8, marginBottom:8, border:`1px solid ${isExpired(d.expiry_date)?"#e24b4a40":isExpiringSoon(d.expiry_date)?"#e6821e40":"#f5f5f5"}` }}>
            <div style={{ width:36, height:36, background:`${docTypeColor[d.type]}20`, borderRadius:8, display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, flexShrink:0 }}>
              {d.type==="license"?<BadgeIcon size={18} color={docTypeColor[d.type]}/>:d.type==="insurance"?<ShieldIcon size={18} color={docTypeColor[d.type]}/>:d.type==="registration"?<DocumentIcon size={18} color={docTypeColor[d.type]}/>:<NoteIcon size={18} color={docTypeColor[d.type]}/>}
            </div>
            <div style={{ flex:1 }}>
              <div style={{ fontSize:13, fontWeight:500, color:"#000000", textTransform:"capitalize" }}>{d.type}</div>
              {d.expiry_date&&(
                <div style={{ fontSize:11, color:isExpired(d.expiry_date)?"#e24b4a":isExpiringSoon(d.expiry_date)?"#e6821e":"#555", marginTop:2 }}>
                  {isExpired(d.expiry_date)?"Expired":"Expires"}: {new Date(d.expiry_date).toLocaleDateString()}
                  {isExpiringSoon(d.expiry_date)&&<WarningIcon size={11} color="#e6821e"/>}
                </div>
              )}
              {d.is_verified&&<div style={{ fontSize:10, color:"#1d9e75", marginTop:2 }}>✓ Verified</div>}
            </div>
            <div style={{ display:"flex", gap:6 }}>
              {d.document_url&&<a href={d.document_url} target="_blank" rel="noreferrer" style={{ background:"#eff6ff", border:"1px solid #378add40", borderRadius:6, color:"#378add", fontSize:11, padding:"4px 8px", textDecoration:"none" }}>View</a>}
              <button onClick={()=>deleteDoc(d.id)} style={{ background:"none", border:"1px solid #e24b4a40", borderRadius:6, color:"#e24b4a", fontSize:11, padding:"4px 8px", cursor:"pointer" }}>Remove</button>
            </div>
          </div>
        ))}

        <div style={{ marginTop:"1rem", paddingTop:"1rem", borderTop:"1px solid #eeeeee" }}>
          <div style={{ fontFamily:"Syne", fontSize:13, fontWeight:700, marginBottom:"0.75rem", color:"#000000" }}>Add document</div>
          <form onSubmit={addDocument}>
            <div style={{ display:"grid", gridTemplateColumns:isMobile?"1fr":"1fr 1fr", gap:10 }}>
              <div>
                <label style={lbl}>Document type</label>
                <select style={inp} value={docForm.type} onChange={e=>setDocForm(f=>({...f,type:e.target.value}))}>
                  <option value="license">Driver license</option>
                  <option value="insurance">Insurance</option>
                  <option value="registration">Vehicle registration</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label style={lbl}>Expiry date</label>
                <input style={inp} type="date" value={docForm.expiry_date} onChange={e=>setDocForm(f=>({...f,expiry_date:e.target.value}))}/>
              </div>
            </div>
            
            <div style={{ marginBottom:10 }}>
              <label style={{ fontSize:11, color:"#666", textTransform:"uppercase", letterSpacing:"0.05em", display:"block", marginBottom:4 }}>Upload file (PDF, JPG, PNG) *</label>
              <input type="file" accept="image/*,.pdf" onChange={e=>setDocForm(f=>({...f,file:e.target.files[0]}))}
                style={{ width:"100%", fontSize:12, color:"#555", fontFamily:"DM Sans,sans-serif" }}/>
              {docForm.file&&<div style={{ fontSize:11, color:"#1d9e75", marginTop:4 }}>✓ {docForm.file.name}</div>}
            </div>
            <button type="submit" disabled={docUploading}
              style={{ background:docUploading?"#ccc":"#e6821e", border:"none", borderRadius:8, color:"#fff", fontFamily:"Syne,sans-serif", fontSize:13, fontWeight:700, padding:"10px 20px", cursor:docUploading?"not-allowed":"pointer" }}>
              {docUploading?"Uploading...":"Upload document"}
            </button>
          </form>
        </div>
      </div>
    </div>
  )
}







